import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import styles from "./AppointmentFormModal.module.scss";

const defaultState = {
  appointment_type_id: "",
  counsellor_id: "",
  enquiry_id: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  notes_internal: "",
  start_time: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
};

export default function AppointmentFormModal({
  show,
  onHide,
  types = [],
  counsellors = [],
  initialValues = {},
  onSubmit,
  submitLabel = "Confirm booking",
}) {
  const [form, setForm] = useState(defaultState);

  useEffect(() => {
    const nextState = {
      ...defaultState,
      ...initialValues,
      start_time: initialValues?.start_time || "",
      timezone: initialValues?.timezone || defaultState.timezone,
    };
    setForm(nextState);
  }, [initialValues, show]);

  const selectedType = useMemo(
    () => types.find((item) => Number(item.id) === Number(form.appointment_type_id)),
    [types, form.appointment_type_id]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onSubmit?.(form);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Appointment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Appointment Type</Form.Label>
                <Form.Select
                  name="appointment_type_id"
                  value={form.appointment_type_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select type</option>
                  {types.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.duration_minutes}m)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Counsellor</Form.Label>
                <Form.Select name="counsellor_id" value={form.counsellor_id} onChange={handleChange}>
                  <option value="">Unassigned</option>
                  {counsellors.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.username || item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Customer Name</Form.Label>
                <Form.Control
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  placeholder="Full name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Customer Email</Form.Label>
                <Form.Control
                  name="customer_email"
                  type="email"
                  value={form.customer_email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Customer Phone</Form.Label>
                <Form.Control
                  name="customer_phone"
                  value={form.customer_phone}
                  onChange={handleChange}
                  placeholder="+61 4xx xxx xxx"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Enquiry ID (optional)</Form.Label>
                <Form.Control name="enquiry_id" value={form.enquiry_id} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start Date & Time</Form.Label>
                <Form.Control
                  name="start_time"
                  type="datetime-local"
                  value={form.start_time}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Timezone</Form.Label>
                <Form.Control name="timezone" value={form.timezone} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Internal Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes_internal"
                  value={form.notes_internal}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <small className={styles.muted}>
                Duration auto-applies from appointment type:{" "}
                <strong>{selectedType ? `${selectedType.duration_minutes} minutes` : "--"}</strong>
              </small>
            </Col>
          </Row>
          <div className={styles.actions}>
            <Button variant="outline-secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit">{submitLabel}</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
